import React, { useState, useEffect } from 'react';
import { CreditCard, Check, Clock, X, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useSubscription() {
  const { token, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || user?.role !== 'lender') {
      setLoading(false);
      return;
    }
    fetch(`${API}/subscriptions/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStatus(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, user]);

  return { status, loading, isSubscribed: status?.subscribed === true };
}

export function SubscriptionBanner() {
  const { token } = useAuth();
  const { status, loading, isSubscribed } = useSubscription();
  const [starting, setStarting] = useState(false);

  if (loading || isSubscribed) return null;

  const handleSubscribe = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to start checkout');
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-blue-500/20 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-blue-500/5 p-5 mb-5"
      data-testid="subscription-banner"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Lender Subscription Required</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Subscribe for <span className="text-white font-semibold">$55/month</span> to place bids on loan requests. Browse freely — cancel anytime.
            </p>
          </div>
        </div>
        <Button
          onClick={handleSubscribe}
          disabled={starting}
          className="bg-gradient-to-r from-blue-500 to-purple-500 hover:brightness-110 whitespace-nowrap"
          data-testid="subscribe-btn"
        >
          {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
          Subscribe Now
        </Button>
      </div>
    </div>
  );
}

export function SubscriptionGate({ children }) {
  const { token } = useAuth();
  const { isSubscribed, loading } = useSubscription();
  const [starting, setStarting] = useState(false);

  if (loading) return null;
  if (isSubscribed) return children;

  const handleSubscribe = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API}/subscriptions/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="p-6 rounded-2xl border border-orange-500/15 bg-orange-500/5 text-center" data-testid="subscription-gate">
      <AlertTriangle className="w-8 h-8 text-orange-400 mx-auto mb-3" />
      <h3 className="font-semibold text-lg mb-1">Subscription Required to Bid</h3>
      <p className="text-sm text-gray-400 mb-4 max-w-md mx-auto">
        You need an active subscription ($55/month) to place bids on loan requests.
        You can browse all requests freely. Cancel anytime — non-refundable.
      </p>
      <Button
        onClick={handleSubscribe}
        disabled={starting}
        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:brightness-110"
        data-testid="subscribe-gate-btn"
      >
        {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
        Subscribe for $55/month
      </Button>
    </div>
  );
}

export function SubscriptionStatus() {
  const { status, loading } = useSubscription();

  if (loading || !status) return null;

  if (status.subscribed) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20" data-testid="subscription-active">
        <Check className="w-4 h-4 text-green-400" />
        <span className="text-xs text-green-400 font-medium">Subscribed</span>
        {status.expires_at && (
          <span className="text-xs text-gray-500">
            until {new Date(status.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-500/10 border border-gray-500/20" data-testid="subscription-inactive">
      <X className="w-4 h-4 text-gray-400" />
      <span className="text-xs text-gray-400">No subscription</span>
    </div>
  );
}
